<template>
	<div click.stop>
		<div class="modal is-active">
			<div class="modal-background" @click="cancel"></div>
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">
						{{ localUser.id ? $t('add_user.edit_user') : $t('add_user.new_user') }}
					</p>
					<button class="delete" aria-label="close" @click="cancel"></button>
				</header>
				<section class="modal-card-body no-scroll">
					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('add_user.first_name_label') }}</label>
							<div class="control">
								<input
									:disabled="!canModifyUser"
									type="text"
									v-model="localUser.firstName"
									class="input"
									:placeholder="$t('add_user.first_name_placeholder')"
									autocomplete="off"
								/>
							</div>
						</div>
						<div class="field column">
							<label class="label">	{{ $t('add_user.last_name_label') }}</label>
							<div class="control">
								<input
									:disabled="!canModifyUser"
									type="text"
									v-model="localUser.lastName"
									class="input"
									:placeholder="$t('add_user.last_name_placeholder')"
									autocomplete="off"
								/>
							</div>
						</div>
					</div>

					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('add_user.email_label') }}</label>
							<div
								class="control"
								:class="{
									'is-disabled': localUser.email && !validUserEmail
								}"
							>
								<input
									:disabled="localUser.id || !canModifyUser"
									type="email"
									v-model="localUser.email"
									class="input"
									:placeholder="$t('add_user.email_placeholder')"
									autocomplete="off"
								/>
								<span
									class="icon"
									id="invalid-email-icon"
									v-if="localUser.email && !validUserEmail"
								>
									<i class="fas fa-exclamation-circle" />
								</span>
							</div>
						</div>
						<div class="field column">
							<label class="label">{{ $t('add_user.phone_label') }}</label>
							<div class="control">
								<input
									:disabled="!canModifyUser"
									type="text"
									v-model="localUser.phoneNumber"
									class="input"
									:placeholder="$t('add_user.phone_placeholder')"
									autocomplete="off"
								/>
							</div>
						</div>

						<div class="field column">
						<label class="label">{{ $t('add_user.language_label') }}</label>
						<div class="control">
							<div class="dropdown is-active">
							<div class="dropdown-trigger">
								<button class="button" @click="toggleLanguageDropdown">
								<img :src="selectedLanguage.icon" class="flag-icon" />
								<span class="icon is-small">
									<i class="fas fa-angle-down" />
								</span>
								</button>
							</div>
							<div class="dropdown-menu lang-switcher" v-if="languageDropdownVisible">
								<div class="dropdown-content">
								<a
									v-for="lang in languages"
									:key="lang.code"
									class="dropdown-item"
									@click="selectLanguage(lang.code)"
								>
									<img :src="lang.icon" class="flag-icon" />
								</a>
								</div>
							</div>
							</div>
						</div>
						</div>

					</div>

					<div v-if="!localUser.id" class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('add_user.password_label') }}</label>
							<div class="control">
								<input
									type="password"
									v-model="password"
									class="input"
									:placeholder="$t('add_user.password_placeholder')"
									autocomplete="off"
								/>
							</div>
						</div>
						<div class="field column">
							<label class="label">{{ $t('add_user.repeat_password_label') }}</label>
							<div class="control">
								<input
									type="password"
									v-model="repeatPassword"
									class="input"
									:class="password != repeatPassword ? 'is-danger' : ''"
									:placeholder="$t('add_user.password_placeholder')"
									autocomplete="off"
								/>
							</div>
						</div>
					</div>
					<div class="field" style="margin-top: 12px">
						<label class="label">{{ $t('add_user.roles_label') }}</label>
						<div class="control">
							<v-select
								:disabled="!canModifyUser"
								class="panel-select user"
								v-model="selectedRoles"
								:multiple="true"
								:options="roles"
								:placeholder="$t('add_user.select_roles_placeholder')"
								:allowEmpty="true"
								:internalSearch="true"
								:showLabels="false"
								:hideSelected="true"
								:closeOnSelect="false"
								trackBy="id"
								label="name"
							>
								<span slot="noResult">{{ $t('add_user.no_results') }}</span>
								<span
									slot="caret"
									slot-scope="{ toggle }"
									class="caret"
									@mousedown.prevent="toggle()"
								><i class="fas fa-caret-down caret-inner"></i
								></span>
								<span
									slot="tag"
									slot-scope="{ option, remove }"
									@mousedown.prevent
								><span class="custom__tag">
									{{ option.name
									}}<span
										class="custom__remove"
										@click.prevent="remove(option)"
									>
										<i class="fas fa-times fa-sm"></i></span
									></span>
								</span>
							</v-select>
						</div>
					</div>
					<div class="field" style="margin-top: 12px" v-if="externalRoles?.length > 0 || selectedExternalRoles?.length > 0">
						<label class="label">{{ $t('add_user.external_roles_label') }}</label>
						<div class="control">
							<v-select
								:disabled="!canModifyUser || !$isAllowedTo('users_manage_external_roles')"
								class="panel-select user"
								v-model="selectedExternalRoles"
								:multiple="true"
								:options="externalRoles"
								:placeholder="$t('add_user.select_roles_placeholder')"
								:allowEmpty="true"
								:internalSearch="true"
								:showLabels="false"
								:hideSelected="true"
								:closeOnSelect="false"
								trackBy="id"
								label="name"
							>
								<span slot="noResult">{{ $t('add_user.no_results') }}</span>
								<span
									slot="caret"
									slot-scope="{ toggle }"
									class="caret"
									@mousedown.prevent="toggle()"
								><i class="fas fa-caret-down caret-inner"></i
								></span>
								<span
									slot="tag"
									slot-scope="{ option, remove }"
									@mousedown.prevent
								><span class="custom__tag">
									{{ option.name
									}}<span
										class="custom__remove"
										@click.prevent="remove(option)"
									>
										<i class="fas fa-times fa-sm"></i></span
									></span>
								</span>
							</v-select>
						</div>
					</div>
					<div>
						<span v-if="error" class="tag is-danger is-medium is-light mt-2">{{
							error
						}}</span>
					</div>
					<p v-if="successFeedback" class="has-text-success is-medium mt-5">
						{{ successFeedback }}
					</p>
				</section>
				<footer class="modal-card-foot">
					<div class="column flex">
						<button
							class="button is-dark"
							:class="isLoading ? 'is-loading' : ''"
							:disabled="disableSave"
							@click="onSubmit"
						>
							<span class="icon is-info"><i class="fas fa-save"/></span
							><span>{{ $t('add_user.save_button') }}</span>
						</button>
						<button
							v-if="localUser.id && $isAllowedTo('users_edit')"
							class="button is-dark"
							id="reset-pwd-button"
							@click="resetPassword"
						>
							<span class="icon is-info"><i class="fas fa-lock"/></span>
							<p>{{ $t('add_user.reset_password') }}</p>
						</button>
						<div class="select" style="text-align: right; float: right">
							<select v-model="localUser.userRole" :disabled="!canChangeSystemRole" :aria-label="$t('add_user.system_role')">
								<option v-for="role in claimRoles" :value="getAccessLevel(role)" :key="role">{{ getAccessLevelName(role) }}</option>
								<option v-if="currentUserMissingPrivilege" :value="localUser.userRole">{{ currentSystemRoleLabel }}</option>
							</select>
						</div>
					</div>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { email as emailValidator } from '@/validators/validation-logic';

const initialUserState = () => ({
	firstName: '',
	lastName: '',
	email: '',
	phoneNumber: '',
	userRole: 'User',
	roleIds: [],
	externalRoleIds: [],
	preferredLanguageCode: null
});

export default {
	props: {
		user: {
			type: Object,
			default: () => ({})
		}
	},

	data: () => ({
		roles: [],
		externalRoles: [],
		claimRoles: [],

		localUser: initialUserState(),
		originalUserRole: null,
		userLoaded: false,

		selectedRoles: [],
		selectedExternalRoles: [],
		password: '',
		repeatPassword: '',

		isLoading: false,
		error: '',
		successFeedback: '',

		languageDropdownVisible: false,
		languages: [
		{ code: 'sv', icon: require('@/assets/icons/se.svg') },
		{ code: 'en', icon: require('@/assets/icons/en.svg') },
		{ code: 'fi', icon: require('@/assets/icons/fi.svg') }
		]
	}),

	computed: {
		...mapGetters('user', ['getActiveCustomer', 'isSbaTechnician']),

		canModifyUser() {
			return this.localUser.id
				? this.userLoaded && this.$isAllowedTo('users_edit')
				: this.$isAllowedTo('users_create');
		},

		validPassword() {
			if (this.localUser.id) {
				return true;
			} else {
				return this.password != '' && this.password == this.repeatPassword;
			}
		},

		disableSave() {

    return (
		!this.canModifyUser || this.isLoading ||
		(this.localUser.id && this.localUser.userRole !== this.originalUserRole && !this.canChangeSystemRole) ||
        this.localUser.firstName == '' ||
        this.localUser.lastName == '' ||
        !this.validPassword ||
        this.localUser.phoneNumber == '' ||
        this.localUser.email == '' ||
        !this.validUserEmail ||
        (this.currentUserMissingPrivilege && !this.canSaveWithoutSystemRoleChange)
    );
},

		currentUserMissingPrivilege() {
			if (!Array.isArray(this.claimRoleNames) || this.claimRoleNames.length === 0) {
				return true;
			}
			const norm = v => (v ?? '').toString().trim().toLowerCase();

			const userRole = norm(this.localUser?.userRole);
			if (!userRole) return true; // no role -> missing

			return !this.claimRoleNames.some(r => norm(r) === userRole);
		},

		canSaveWithoutSystemRoleChange() {
			return this.isSbaTechnician && Boolean(this.localUser.id) &&
				this.localUser.userRole === this.originalUserRole;
		},

		canChangeSystemRole() {
			return this.canModifyUser && !this.currentUserMissingPrivilege &&
				(!this.localUser.id || this.claimRoleNames.includes(this.originalUserRole));
		},

		currentSystemRoleLabel() {
			const role = this.localUser.userRole;
			const index = Array.from({ length: 8 }, (_, i) => i)
				.find(i => this.getAccessLevel(i)?.toLowerCase() === role?.toLowerCase());
			return index !== undefined ? this.getAccessLevelName(index)
				: role || this.$t('add_user.system_role_unavailable');
		},


		validUserEmail() {
			return this.validEmail(this.localUser.email);
		},

		claimRoleNames() {
			return this.claimRoles.map(x => this.getAccessLevel(x));
		},

		updatedRoleIds() {
			const editableRoles = [
				...this.roles,
				...(this.$isAllowedTo('users_manage_external_roles') ? this.externalRoles : [])
			];
			const editableIds = new Set(editableRoles.map(role => role.id));
			// Keep assignments outside the roles available in this customer view.
			const preservedIds = (this.localUser.roles ?? [])
				.filter(role => !editableIds.has(role.id)).map(role => role.id);
			return [...new Set([
				...preservedIds,
				...this.selectedRoles.map(role => role.id),
				...(this.$isAllowedTo('users_manage_external_roles')
					? this.selectedExternalRoles.map(role => role.id) : [])
			])];
		},

		selectedLanguage() {
			return this.languages.find(lang => lang.code === this.localUser.preferredLanguageCode) || this.languages[0];
		}
	},

	async created() {
		this.localUser = {
			...this.localUser,
			...this.user
		};

		if (this.user.id) {
			await this.fetchUser(this.user.id);
		}

		await this.fetchRoles();

		this.selectedRoles = this.localUser?.roles?.filter(x => x.customerId === this.getActiveCustomer.id) ?? [];

		let claimRoles = await this.$services.user.getAvailableRolesForUser({
			loading: val => this.isLoading = val
		});

		this.claimRoles = claimRoles;

		if (this.user.id) {
			try {
				this.externalRoles = await this.$services.role.getExternalRoles(this.user.id, this.getActiveCustomer.id, {
					loading: val => this.isLoading = val
				});

				this.selectedExternalRoles = this.externalRoles?.filter(x => this.localUser.roles.some(y => y.id === x.id)) ?? [];
			} catch (error) {
				console.error(error);
			}
		}
	},

	methods: {
		onSubmit() {
			if (this.disableSave) return;

			this.successFeedback = '';
			this.error = '';

			if (this.localUser.id) {
				this.updateUser();

				return;
			}

			this.createUser();
		},

		cancel() {
			this.$emit('cancel');
		},

		async fetchRoles() {
			this.roles = await this.$services.role.list({
				customerId: this.getActiveCustomer?.id,
				pageSize: 0
			});
		},

		async createUser() {
			try {
				await this.$services.user.create(
					{
						email: this.localUser.email,
						userName: this.localUser.email,
						firstName: this.localUser.firstName,
						lastName: this.localUser.lastName,
						emailConfirmed: true,
						password: this.password,
						applicationId: 'string',
						phoneNumber: this.localUser.phoneNumber,
						companyId: this.getActiveCustomer.companyId,
						customerId: this.getActiveCustomer?.id,
						userRole: this.localUser.userRole,
						country: 'Swe',
						roleIds: [...this.selectedRoles.map(r => r.id), ...this.selectedExternalRoles.map(x => x.id)],
						preferredLanguageCode: this.localUser.preferredLanguageCode
					},
					{
						loading: val => {
							this.isLoading = val;
						}
					}
				);

				this.successFeedback = this.$t('add_user.user_created');
				this.localUser = initialUserState();
				this.password = '';
				this.repeatPassword = '';
				this.$emit('on-update');
			} catch (error) {
				this.error = error || this.$t('add_user.user_create_error');
			}
		},

		async updateUser() {
			try {
				await this.$services.user.update(
					this.localUser.id,
					{
						email: this.localUser.email,
						userName: this.localUser.userName,
						firstName: this.localUser.firstName,
						lastName: this.localUser.lastName,
						phoneNumber: this.localUser.phoneNumber,
						companyId: this.localUser.companyId,
						customerId: this.localUser.customerId,
						identityId: this.localUser.identityId,
						roleIds: this.updatedRoleIds,
						preferredLanguageCode: this.localUser.preferredLanguageCode
					},
					{
						loading: val => {
							this.isLoading = val;
						}
					}
				);

				if (this.localUser.userRole !== this.originalUserRole) {
					await this.$services.user.updateRoles(
					this.localUser.id,
					{
						role: this.localUser.userRole
					},
					{
						loading: val => {
							this.isLoading = val;
						}
					}
				);
					this.originalUserRole = this.localUser.userRole;
				}

				this.successFeedback = this.$t('add_user.user_updated');
				this.$emit('on-update');
			} catch (error) {
				this.error = error || this.$t('add_user.user_update_error');
			}
		},

		async resetPassword() {
			this.error = '';
			
			await this.$services.user.resetPassword({
				email: this.localUser.email
			});

			this.successFeedback = this.$t('add_user.reset_password_success');
		},

		async fetchUser(userId) {
			try {
				const data = await this.$services.user.get(userId);
				this.localUser = {
					...this.localUser,
					...data
				};
				this.originalUserRole = this.localUser.userRole;
				this.userLoaded = true;
			} catch (error) {
				this.error = error;
			}
		},

		validEmail(email) {
			return emailValidator()(email) === true;
		},

		toggleLanguageDropdown() {
			this.languageDropdownVisible = !this.languageDropdownVisible;
		},
		
		selectLanguage(code) {
			this.localUser.preferredLanguageCode = code;
			this.languageDropdownVisible = false;
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.column.flex {
  display: flex;
}
.email {
  margin-top: 0.5rem;
  color: white;
}

.multiselect.panel-select.user {
  height: auto;
  max-width: none;

  .custom__tag {
    display: inline-block;
    margin-bottom: 5px;
  }
}

#reset-pwd-button {
  > p {
    padding-top: 4px !important;
  }
}

.flag-icon {
  width: 24px;
  height: 16px;
  margin-right: 8px;
  vertical-align: middle;
}

.dropdown-menu {
    display: none;
    left: 0;
    min-width: 40px;
    padding-top: 4px;
    position: absolute;
    top: 100%;
    z-index: 20;
}

.field {
  .control {
    &.is-disabled {
      input {
        border: 1px solid $danger;
        box-shadow: inset 0 0 5px rgba($color: $info, $alpha: 0.8);
      }
    }

    #invalid-email-icon {
      height: fit-content;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%) !important;
      right: 1rem;

      > i {
        color: $danger;
      }
    }
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.user .multiselect__tags {
  white-space: normal;
}
</style>
